import type { ExpressionSpecification, LayerSpecification } from "maplibre-gl";
import {
  BOUNDARY_COLOR,
  BOUNDARY_MARITIME_COLOR,
  FONT_BOLD,
  FONT_REGULAR,
  IS_LINE,
  IS_POINT,
  IS_POLYGON,
  LABEL_COLOR,
  LABEL_HALO_COLOR,
  LABEL_SECONDARY_COLOR,
  LAND_COLOR,
  PARK_FILL_COLOR,
  PARK_LABEL_COLOR,
  PARK_LINE_COLOR,
  ROAD_COLOR,
  WATER_FILL_COLOR,
  WATER_LABEL_COLOR,
  WATER_LINE_COLOR,
} from "./constants.ts";

export const SOURDOUGH = "sourdough";
export const HILLSHADE_SOURCE = "hillshade";

const background: LayerSpecification = {
  "id": "background",
  "type": "background",
  "paint": { "background-color": LAND_COLOR },
};

const IS_PROTECTED_AREA: ExpressionSpecification = [
  "in",
  ["get", "boundary"],
  ["literal", ["national_park", "protected_area"]],
];

const IS_PARK: ExpressionSpecification = [
  "in",
  ["get", "leisure"],
  ["literal", ["park", "nature_reserve"]],
];

const area = (
  id: string,
  sourceLayer: string,
  filter: ExpressionSpecification,
  minzoom: number,
): LayerSpecification[] => [
  {
    "id": `${id}-fill`,
    "type": "fill",
    "source": SOURDOUGH,
    "source-layer": sourceLayer,
    "minzoom": minzoom,
    "filter": ["all", IS_POLYGON, filter],
    "paint": {
      "fill-color": PARK_FILL_COLOR,
      "fill-opacity": 0.75,
    },
  },
  {
    "id": `${id}-outline`,
    "type": "line",
    "source": SOURDOUGH,
    "source-layer": sourceLayer,
    "minzoom": minzoom,
    "filter": ["all", IS_POLYGON, filter],
    "paint": {
      "line-color": PARK_LINE_COLOR,
      "line-width": ["interpolate", ["linear"], ["zoom"], minzoom, 0.5, 14, 1.25],
    },
  },
];

const hillshade: LayerSpecification = {
  "id": "hillshade",
  "type": "raster",
  "source": HILLSHADE_SOURCE,
  "paint": {
    "raster-opacity": ["interpolate", ["linear"], ["zoom"], 12, 0.3, 18, 0.08],
  },
};

const water: LayerSpecification = {
  "id": "water",
  "type": "fill",
  "source": SOURDOUGH,
  "source-layer": "water",
  "filter": IS_POLYGON,
  "paint": {
    "fill-color": WATER_FILL_COLOR,
    "fill-opacity": 0.65,
  },
};

const waterOutline: LayerSpecification = {
  "id": "water-outline",
  "type": "line",
  "source": SOURDOUGH,
  "source-layer": "water",
  "filter": IS_POLYGON,
  "paint": {
    "line-color": WATER_LINE_COLOR,
    "line-width": ["interpolate", ["linear"], ["zoom"], 4, 0.5, 12, 1],
  },
};

const waterway = (
  id: string,
  kind: string,
  minzoom: number,
  width: [number, number],
): LayerSpecification => ({
  "id": id,
  "type": "line",
  "source": SOURDOUGH,
  "source-layer": "waterways",
  "minzoom": minzoom,
  "filter": ["all", IS_LINE, ["==", ["get", "waterway"], kind]],
  "paint": {
    "line-color": WATER_LINE_COLOR,
    "line-width": [
      "interpolate",
      ["exponential", 1.5],
      ["zoom"],
      minzoom,
      0,
      minzoom + 0.5,
      width[0],
      18,
      width[1],
    ],
  },
});

const waterLabels: LayerSpecification = {
  "id": "water-labels",
  "type": "symbol",
  "source": SOURDOUGH,
  "source-layer": "water",
  "minzoom": 8,
  "filter": ["all", IS_POINT, ["has", "name"], [">=", ["zoom"], ["get", "_reczoom"]]],
  "layout": {
    "text-field": ["get", "name"],
    "text-font": FONT_REGULAR,
    "text-size": 13,
    "text-max-width": 8,
    "text-padding": 5,
  },
  "paint": {
    "text-color": WATER_LABEL_COLOR,
    "text-halo-color": "rgba(255, 255, 255, 0.85)",
    "text-halo-width": 1.5,
    "text-halo-blur": 1,
  },
};

const waterwayLabels: LayerSpecification = {
  "id": "waterway-labels",
  "type": "symbol",
  "source": SOURDOUGH,
  "source-layer": "waterways",
  "minzoom": 12,
  "filter": [
    "all",
    IS_LINE,
    ["in", ["get", "waterway"], ["literal", ["river", "stream", "canal"]]],
    ["has", "name"],
  ],
  "layout": {
    "symbol-placement": "line",
    "text-field": ["get", "name"],
    "text-font": FONT_REGULAR,
    "text-size": ["match", ["get", "waterway"], "river", 13, 11],
    "symbol-spacing": 400,
    "text-max-angle": 30,
  },
  "paint": {
    "text-color": WATER_LABEL_COLOR,
    "text-halo-color": "rgba(255, 255, 255, 0.85)",
    "text-halo-width": 1.5,
    "text-halo-blur": 1,
  },
};

const isAdminLine = (level: number): ExpressionSpecification => [
  "all",
  IS_LINE,
  ["==", ["get", "boundary"], "administrative"],
  ["==", ["get", "admin_level"], level],
];

const boundaries: LayerSpecification[] = [
  {
    "id": "boundary-country",
    "type": "line",
    "source": SOURDOUGH,
    "source-layer": "boundaries",
    "filter": ["all", isAdminLine(2), ["!=", ["get", "maritime"], "yes"]],
    "paint": {
      "line-color": BOUNDARY_COLOR,
      "line-width": ["interpolate", ["linear"], ["zoom"], 3, 0.75, 10, 1.5],
    },
  },
  {
    "id": "boundary-country-maritime",
    "type": "line",
    "source": SOURDOUGH,
    "source-layer": "boundaries",
    "minzoom": 6,
    "filter": ["all", isAdminLine(2), ["==", ["get", "maritime"], "yes"]],
    "paint": {
      "line-color": BOUNDARY_MARITIME_COLOR,
      "line-dasharray": [10, 10],
      "line-width": 0.75,
    },
  },
  {
    "id": "boundary-state",
    "type": "line",
    "source": SOURDOUGH,
    "source-layer": "boundaries",
    "filter": ["all", isAdminLine(4), ["!=", ["get", "maritime"], "yes"]],
    "paint": {
      "line-color": BOUNDARY_COLOR,
      "line-width": ["interpolate", ["linear"], ["zoom"], 3, 0.4, 10, 1],
      "line-dasharray": [4, 2],
    },
  },
  {
    "id": "boundary-county",
    "type": "line",
    "source": SOURDOUGH,
    "source-layer": "boundaries",
    "minzoom": 8,
    "filter": ["all", isAdminLine(6), ["!=", ["get", "maritime"], "yes"]],
    "paint": {
      "line-color": "rgba(70, 80, 90, 0.2)",
      "line-width": 0.75,
      "line-dasharray": [3, 2],
    },
  },
];

const MAJOR = ["motorway", "trunk", "primary"];
const MINOR = ["secondary", "tertiary"];
const LOCAL = ["residential", "unclassified", "living_street"];

const widthFactor: ExpressionSpecification = [
  "match",
  ["get", "highway"],
  ["motorway", "trunk"],
  1.6,
  "primary",
  1.3,
  ["secondary", "tertiary"],
  1.0,
  0.7,
];

const isRoad = (classes: string[]): ExpressionSpecification => [
  "all",
  IS_LINE,
  ["in", ["get", "highway"], ["literal", classes]],
];

const roads = (id: string, classes: string[], minzoom: number): LayerSpecification => ({
  "id": id,
  "type": "line",
  "source": SOURDOUGH,
  "source-layer": "highways",
  "minzoom": minzoom,
  "filter": isRoad(classes),
  "layout": { "line-cap": "round", "line-join": "round" },
  "paint": {
    "line-color": ROAD_COLOR,
    "line-width": [
      "interpolate",
      ["exponential", 1.5],
      ["zoom"],
      minzoom,
      ["*", widthFactor, 0.5],
      18,
      ["*", widthFactor, 8],
    ],
  },
});

const roadLabels: LayerSpecification = {
  "id": "road-labels",
  "type": "symbol",
  "source": SOURDOUGH,
  "source-layer": "highways",
  "minzoom": 14,
  "filter": ["all", isRoad([...MAJOR, ...MINOR, ...LOCAL]), ["has", "name"]],
  "layout": {
    "symbol-placement": "line",
    "text-field": ["get", "name"],
    "text-font": FONT_REGULAR,
    "text-size": 11,
    "symbol-spacing": 350,
    "text-max-angle": 30,
  },
  "paint": {
    "text-color": LABEL_SECONDARY_COLOR,
    "text-halo-color": LABEL_HALO_COLOR,
    "text-halo-width": 1.5,
  },
};

const populationFactor: ExpressionSpecification = [
  "case",
  [">", ["coalesce", ["get", "population"], 0], 500_000],
  1.5,
  [">", ["coalesce", ["get", "population"], 0], 150_000],
  1.2,
  1.0,
];

const cities: LayerSpecification = {
  "id": "places-cities",
  "type": "symbol",
  "source": SOURDOUGH,
  "source-layer": "places",
  "filter": ["in", ["get", "place"], ["literal", ["city", "town"]]],
  "layout": {
    "text-field": ["get", "name"],
    "text-font": [
      "match",
      ["get", "place"],
      "city",
      ["literal", FONT_BOLD],
      ["literal", FONT_REGULAR],
    ],
    "text-size": [
      "interpolate",
      ["linear"],
      ["zoom"],
      3,
      ["*", populationFactor, 7],
      6,
      ["*", populationFactor, 9],
      10,
      ["*", populationFactor, 13],
      14,
      ["*", populationFactor, 16],
    ],
    "text-padding": 6,
  },
  "paint": {
    "text-color": LABEL_COLOR,
    "text-halo-color": LABEL_HALO_COLOR,
    "text-halo-width": 1.75,
    "text-halo-blur": 0.5,
  },
};

const villages: LayerSpecification = {
  "id": "places-villages",
  "type": "symbol",
  "source": SOURDOUGH,
  "source-layer": "places",
  "minzoom": 9,
  "filter": ["in", ["get", "place"], ["literal", ["village", "hamlet"]]],
  "layout": {
    "text-field": ["get", "name"],
    "text-font": FONT_REGULAR,
    "text-max-width": 8,
    "text-size": ["interpolate", ["linear"], ["zoom"], 9, 10, 14, 14],
  },
  "paint": {
    "text-color": LABEL_SECONDARY_COLOR,
    "text-halo-color": LABEL_HALO_COLOR,
    "text-halo-width": 1.5,
  },
};

const areaLabels = (
  id: string,
  sourceLayer: string,
  filter: ExpressionSpecification,
  minzoom: number,
): LayerSpecification => ({
  "id": id,
  "type": "symbol",
  "source": SOURDOUGH,
  "source-layer": sourceLayer,
  "minzoom": minzoom,
  "filter": ["all", IS_POINT, filter, ["has", "name"], [">=", ["zoom"], ["get", "_reczoom"]]],
  "layout": {
    "text-field": ["get", "name"],
    "text-font": FONT_REGULAR,
    "text-size": 11,
    "text-max-width": 8,
    "text-padding": 6,
  },
  "paint": {
    "text-color": PARK_LABEL_COLOR,
    "text-halo-color": LABEL_HALO_COLOR,
    "text-halo-width": 1.5,
  },
});

export const BASEMAP_BELOW: LayerSpecification[] = [
  background,
  ...area("protected-area", "boundaries", IS_PROTECTED_AREA, 5),
  ...area("park", "leisure", IS_PARK, 9),
  hillshade,
];

export const BASEMAP_ABOVE: LayerSpecification[] = [
  waterway("water-river", "river", 8, [1, 20]),
  waterway("water-canal", "canal", 10, [1, 14]),
  waterway("water-stream", "stream", 12, [1, 5]),
  water,
  waterOutline,
  ...boundaries,
  roads("roads-local", LOCAL, 13),
  roads("roads-minor", MINOR, 11),
  roads("roads-major", MAJOR, 9),
  waterLabels,
  waterwayLabels,
  roadLabels,
  areaLabels("protected-area-labels", "boundaries", IS_PROTECTED_AREA, 7),
  areaLabels("park-labels", "leisure", IS_PARK, 11),
  villages,
  cities,
];
